import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Panel } from "@/components/Panel";
import { Network, Clock, Database } from "lucide-react";

const SYMS = { USD:"$", EUR:"€", GBP:"£", NGN:"₦", ZAR:"R", KES:"KSh", GHS:"₵", EGP:"£", INR:"₹", BRL:"R$", MXN:"$", AED:"د.إ", SAR:"﷼", JPY:"¥", CNY:"¥", AUD:"A$", CAD:"C$", SGD:"S$" };
const symFor = (code) => SYMS[code] || (code ? `${code} ` : "$");
const fmt = (n, code) => {
  const s = symFor(code);
  const v = n || 0;
  if (v >= 1_000_000_000) return `${s}${(v/1e9).toFixed(2)}B`;
  if (v >= 1_000_000) return `${s}${(v/1e6).toFixed(1)}M`;
  return `${s}${(v/1e3).toFixed(0)}K`;
};
const DIFF_COLOR = { EASY: "text-[#00E676]", MEDIUM: "text-[#FFB000]", HARD: "text-[#FF3B30]" };

export default function Monetization() {
  const [ops, setOps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasReal, setHasReal] = useState(null);
  const [wsCcy, setWsCcy] = useState("USD");

  const load = () => api.get("/opportunities").then(r => setOps(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/dashboard/metrics").then(r => setHasReal(!!r.data?.has_real_sources)).catch(() => setHasReal(false));
    api.get("/workspace").then(r => {
      const code = r.data?.analysis_context?.currency_code;
      if (code) setWsCcy(code.toUpperCase());
    }).catch(() => {});
  }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/opportunities/generate");
      toast.success(`Mapped ${data.length} revenue pathways.`);
      load();
    } catch {
      toast.error("Generation failed");
    }
    setLoading(false);
  };

  const total = ops.reduce((s, o) => s + (o.estimated_revenue || 0), 0);

  return (
    <div className="p-4 sm:p-6 space-y-6 min-w-0" data-testid="monetization-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">MODULE · MONETIZATION MAP</div>
          <h1 className="font-heading font-black text-2xl sm:text-3xl lg:text-4xl tracking-tighter uppercase mt-1 break-words">Data → Revenue</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">Visual pathways from raw data streams through DARA analysis into quantified revenue lines.</p>
        </div>
        <button
          onClick={generate}
          disabled={loading}
          data-testid="generate-opportunities-btn"
          className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:bg-[#E59E00] transition-colors flex items-center gap-2 disabled:opacity-50"
        >
          <Network className={`w-4 h-4 ${loading ? "animate-pulse" : ""}`} />
          {loading ? "MAPPING…" : "MAP OPPORTUNITIES"}
        </button>
      </div>

      {/* Summary bar */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Panel testId="mon-summary-total" className="p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">TOTAL ANNUAL PIPELINE</div>
          <div className="font-mono text-2xl sm:text-3xl lg:text-4xl text-[#FFB000] mt-2 break-words">{total ? fmt(total, wsCcy) : "—"}</div>
          <div className="font-mono text-[10px] text-zinc-600 mt-1">ACROSS {ops.length} OPPORTUNITIES</div>
        </Panel>
        <Panel className="p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">FASTEST TIME-TO-VALUE</div>
          <div className="font-mono text-2xl sm:text-3xl lg:text-4xl text-white mt-2">
            {ops.length ? `${Math.min(...ops.map(o => o.time_to_value_weeks))}w` : "—"}
          </div>
          <div className="font-mono text-[10px] text-zinc-600 mt-1">SHORTEST PATH TO REVENUE</div>
        </Panel>
        <Panel className="p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">EASY WINS</div>
          <div className="font-mono text-2xl sm:text-3xl lg:text-4xl text-[#00E676] mt-2">
            {ops.filter(o => o.difficulty === "EASY").length}
          </div>
          <div className="font-mono text-[10px] text-zinc-600 mt-1">LOW-EFFORT HIGH-ROI PLAYS</div>
        </Panel>
      </div>

      {hasReal === false ? (
        <Panel testId="mon-need-source" className="p-12 text-center">
          <Database className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
          <h3 className="font-heading font-bold text-xl mb-2">Connect real data first.</h3>
          <p className="text-sm text-zinc-500 mb-6 max-w-lg mx-auto">
            Monetization Map traces real columns → AI process → revenue. Connect at least one CSV / Snowflake / Salesforce source so the pathways tie to your business.
          </p>
          <Link to="/app/sources" data-testid="mon-add-source-cta" style={{ backgroundColor: "var(--brand-accent, #FFB000)", color: "var(--brand-accent-text, #000)" }} className="inline-flex items-center gap-2 font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 transition-opacity">
            <Database className="w-3.5 h-3.5" /> ADD A DATA SOURCE
          </Link>
        </Panel>
      ) : ops.length === 0 ? (
        <Panel testId="mon-empty" className="p-12 text-center">
          <Network className="w-10 h-10 text-zinc-800 mx-auto mb-4" />
          <h3 className="font-heading font-bold text-xl mb-2">No pathways mapped yet.</h3>
          <p className="text-sm text-zinc-500 mb-6">Run the DARA mapping engine to visualize your revenue opportunities.</p>
          <button onClick={generate} disabled={loading} className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:bg-[#E59E00]">
            {loading ? "MAPPING…" : "Map opportunities"}
          </button>
        </Panel>
      ) : (
        <div className="space-y-4">
          {ops.map(o => (
            <div key={o.id} className="panel p-4 sm:p-6" data-testid="opportunity-card">
              <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-bold text-lg sm:text-xl mb-2">{o.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed max-w-2xl">{o.description}</p>
                </div>
                <div className="grid grid-cols-3 gap-3 sm:gap-4 w-full sm:w-auto sm:flex">
                  <div className="text-left sm:text-right">
                    <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600">ANNUAL</div>
                    <div className="font-mono text-base sm:text-2xl text-[#00E676] truncate">{fmt(o.estimated_revenue, o.estimated_revenue_currency || wsCcy)}</div>
                  </div>
                  <div className="text-left sm:text-right">
                    <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600">EFFORT</div>
                    <div className={`font-mono text-sm mt-1 ${DIFF_COLOR[o.difficulty]}`}>{o.difficulty}</div>
                  </div>
                  <div className="text-left sm:text-right">
                    <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600">TTV</div>
                    <div className="font-mono text-sm mt-1 text-zinc-300 flex items-center gap-1 sm:justify-end"><Clock className="w-3 h-3" /> {o.time_to_value_weeks}w</div>
                  </div>
                </div>
              </div>

              {/* Flow diagram — horizontally scrollable on mobile */}
              <div className="flex items-center gap-3 bg-black border border-zinc-900 p-4 overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-4">
                <FlowBox label="DATA IN" items={o.data_inputs} color="#71717A" />
                <Arrow />
                <FlowBox label="DARA ANALYSIS" items={[o.ai_process]} color="#FFB000" accent />
                <Arrow />
                <FlowBox label="REVENUE OUT" items={[o.revenue_output]} color="#00E676" accent />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FlowBox({ label, items, color, accent }) {
  return (
    <div className={`min-w-[140px] border ${accent ? "border-[#FFB000]/30" : "border-zinc-800"} p-3`} style={accent ? { boxShadow: `inset 0 0 0 1px ${color}20` } : {}}>
      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600 mb-2">{label}</div>
      <div className="space-y-1">
        {items.map((it, i) => (
          <div key={i} className="font-mono text-[11px]" style={{ color }}>{it}</div>
        ))}
      </div>
    </div>
  );
}

function Arrow() {
  return (
    <div className="flex-shrink-0 flex items-center text-[#FFB000]">
      <div className="w-8 h-px bg-[#FFB000]/50" />
      <span className="text-lg leading-none -mt-1">►</span>
    </div>
  );
}
